<template>
  <v-card class="pa-4">
    <h2>Full Assignment Compliance Ratio Table</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedInsurer"
          :items="insurers"
          item-text="name"
          item-value="id"
          label="Insurer"
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedDept"
          :items="departments"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedClass"
          :items="filteredFileClasses"
          item-text="subCode"
          item-value="id"
          label="File Classification"
          dense
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-menu v-model="menuMonth" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedMonth"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @change="menuMonth = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div class="mb-2" style="font-weight: bold; font-size: 1.1em">
      {{ tableTitle }}
    </div>
    <!-- TAT row -->
    <div class="mb-2" style="font-weight: bold; color: #1976d2">
      {{ tatText }}
    </div>
    <v-btn
      color="primary"
      class="mt-2 mr-2"
      :disabled="!selectedInsurer || !selectedDept || !selectedMonth"
      @click="fetchTable"
      :loading="loading"
    >
      <v-icon left>mdi-table</v-icon>
      Generate Table
    </v-btn>
    <v-btn
      color="success"
      class="mt-2"
      :disabled="!tableData.length || allZero"
      @click="exportExcel"
      :loading="exporting"
    >
      <v-icon left>mdi-file-excel</v-icon>
      Export Compliance Table
    </v-btn>
    <v-alert v-if="noData || allZero" type="warning" outlined class="mt-4">
      No data available for the selected filters.
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      The exported Excel file will contain the full assignment compliance ratio
      table for your selected filters.
    </v-alert>
    <div v-if="tableData.length && !allZero" class="mt-4">
      <v-simple-table dense>
        <thead>
          <tr>
            <th>Branch</th>
            <th>Complied</th>
            <th>Not Complied</th>
            <th>Total</th>
            <th>Complied %</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in tableData" :key="row.branch">
            <td>
              <strong>{{ row.branch }}</strong>
            </td>
            <td>{{ row.complied }}</td>
            <td>{{ row.notComplied }}</td>
            <td>{{ row.total }}</td>
            <td>{{ row.percent }}</td>
          </tr>
          <tr style="font-weight: bold">
            <td>TOTAL</td>
            <td>{{ totalComplied }}</td>
            <td>{{ totalNotComplied }}</td>
            <td>{{ totalFiles }}</td>
            <td>{{ overallPercent }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";
import { apiConfig } from "../../config/api.config";
import InsDataService from "../../services/InsDataService";
import DeptDataService from "../../services/DeptDataService";

export default {
  name: "ComplianceTable",
  data() {
    return {
      selectedInsurer: null,
      selectedDept: "all",
      selectedClass: "all",
      selectedMonth: null,
      menuMonth: false,
      insurers: [],
      departments: [],
      classificationOptions: [],
      fileClasses: [],
      loading: false,
      exporting: false,
      tableData: [],
      totalComplied: 0,
      totalNotComplied: 0,
      totalFiles: 0,
      overallPercent: "0.00",
      noData: false,
      tatMax: null,
      tatAvailable: false,
      tatExact: false, // <-- add this
    };
  },
  computed: {
    filteredFileClasses() {
      if (this.selectedDept === "all") {
        return this.classificationOptions.filter((c) => c.id === "all");
      }
      const allClass = this.classificationOptions.find((c) => c.id === "all");
      const filtered = this.classificationOptions.filter(
        (c) => c.deptId === this.selectedDept
      );
      return [allClass, ...filtered].filter(Boolean);
    },
    tableTitle() {
      const dept =
        this.departments.find((d) => d.id === this.selectedDept)?.name || "";
      const insurer =
        this.insurers.find((i) => i.id === this.selectedInsurer)?.name || "";
      let fileClass = "";
      if (
        this.selectedClass &&
        this.selectedClass !== "all" &&
        this.fileClasses.length > 0
      ) {
        const fc = this.fileClasses.find(
          (c) => c.value === this.selectedClass || c.id === this.selectedClass
        );
        if (fc) fileClass = fc.text || fc.subCode;
      }
      let title = dept ? dept.toUpperCase() : "";
      if (this.selectedClass && this.selectedClass !== "all" && fileClass) {
        title += ` - ${fileClass.toUpperCase()}`;
      }
      title += " ASSIGNMENT COMPLIANCE RATIO";
      if (insurer) {
        title += ` - "${insurer}"`;
      }
      return title;
    },
    allZero() {
      // True if all rows have total === 0 or tableData is empty
      if (!this.tableData.length) return false;
      return this.tableData.every((row) => row.total === 0);
    },
    tatText() {
      // Only show TAT if a specific file classification is selected (not "all") AND tatExact is true
      if (
        this.tatExact &&
        this.tatAvailable &&
        this.tatMax &&
        this.selectedClass &&
        this.selectedClass !== "all"
      ) {
        const dept =
          this.departments.find((d) => d.id === this.selectedDept)?.name || "";
        let fileClass = "";
        if (
          this.selectedClass &&
          this.selectedClass !== "all" &&
          this.fileClasses.length > 0
        ) {
          const fc = this.fileClasses.find(
            (c) => c.value === this.selectedClass || c.id === this.selectedClass
          );
          if (fc) fileClass = fc.subCode || fc.text;
        }
        return `TAT - ${dept ? dept.toUpperCase() : ""}${
          fileClass ? " - " + fileClass.toUpperCase() : ""
        } - ${this.tatMax} DAYS`;
      } else {
        return "TAT NOT AVAILABLE";
      }
    },
  },
  mounted() {
    this.loadInsurers();
    this.loadDepartments();
    this.loadClassifications();
  },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClass = "all";
    },
    classificationOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
  },
    methods: {
    async loadInsurers() {
      try {
        const res = await InsDataService.getAll();
        // Sort alphabetically and uppercase names
        this.insurers = (res.data || [])
          .map((i) => ({ ...i, name: i.name ? i.name.toUpperCase() : "" }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch {
        this.insurers = [];
      }
    },
    async loadDepartments() {
      try {
        const res = await DeptDataService.getAll();
        this.departments = [
          { id: "all", name: "ALL DEPARTMENTS" },
          ...res.data,
        ].sort((a, b) => a.name.localeCompare(b.name));
      } catch {
        this.departments = [];
      }
    },
    async loadClassifications() {
      try {
        const res = await axios.get(`${apiConfig.baseURL}/subDepts`);
        this.classificationOptions = [
          { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
          ...res.data,
        ].sort((a, b) => {
          if (a.id === "all") return -1;
          if (b.id === "all") return 1;
          return a.subCode.localeCompare(b.subCode);
        });
      } catch {
        this.classificationOptions = [];
      }
    },
    filterClassifications(deptId) {
      if (deptId === "all") {
        this.fileClasses = this.classificationOptions.filter(
          (c) => c.id === "all"
        );
      } else {
        const allClass = this.classificationOptions.find((c) => c.id === "all");
        const filtered = this.classificationOptions.filter(
          (c) => c.deptId === deptId
        );
        this.fileClasses = [allClass, ...filtered].filter(Boolean);
      }
    },
    async fetchTable() {
      this.loading = true;
      this.noData = false;
      this.tableData = [];
      this.totalComplied = 0;
      this.totalNotComplied = 0;
      this.totalFiles = 0;
      this.overallPercent = "0.00";
      this.tatMax = null;
      this.tatAvailable = false;
      this.tatExact = false;
      try {
        const params = {
          insurerId: this.selectedInsurer,
          deptId: this.selectedDept,
          fileClass: this.selectedClass,
          month: this.selectedMonth
            ? require("moment")(this.selectedMonth).format("YYYY-MM")
            : "",
          // Frontend-only override: use 30 days as fallback for Compliance Table
          fallbackTat: 30,
        };
        // Fetch TAT info
        const tatRes = await axios.get(
          `${apiConfig.baseURL}/compliance/compliance-table/tat`,
          { params }
        );
        // Only show TAT if backend confirms it's an exact match for the combo
        if (
          tatRes.data &&
          tatRes.data.tatMax &&
          tatRes.data.exact === true &&
          this.selectedClass &&
          this.selectedClass !== "all"
        ) {
          this.tatMax = tatRes.data.tatMax;
          this.tatAvailable = true;
          this.tatExact = true;
        } else {
          this.tatMax = null;
          this.tatAvailable = false;
          this.tatExact = false;
        }
        // Fetch table data
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/compliance-table/frontend`,
          { params }
        );
        if (res.data && Array.isArray(res.data.rows) && res.data.rows.length) {
          this.tableData = res.data.rows;
          this.totalComplied = res.data.totalComplied;
          this.totalNotComplied = res.data.totalNotComplied;
          this.totalFiles = res.data.totalFiles;
          this.overallPercent = res.data.overallPercent;
          if (this.allZero) {
            this.noData = true;
          } else {
            this.noData = false;
          }
        } else {
          this.noData = true;
        }
      } catch {
        this.noData = true;
        this.tatMax = null;
        this.tatAvailable = false;
        this.tatExact = false;
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          insurerId: this.selectedInsurer,
          deptId: this.selectedDept,
          fileClass: this.selectedClass,
          month: this.selectedMonth
            ? require("moment")(this.selectedMonth).format("YYYY-MM")
            : "",
          titleText: this.tableTitle,
          // Ensure exports use the same frontend-only fallback TAT as the UI table
          fallbackTat: 30,
        };
        const url =
          `${apiConfig.baseURL}/compliance/compliance-table/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
